import { FiClock } from 'react-icons/fi';

// Bank of 30 cue cards segmented by category. Each category corresponds to a
// recurring IELTS Part 2 prompt family (Describe a person / place / object /
// event / experience / future plan). The bank is exposed for downstream UIs
// (Speaking simulator, AI prompts) and keeps the cue cards in one place.
export const speakingPart2CueCardBank = {
  person: [
    'Describe a person who has had a strong influence on you.',
    'Describe a teacher you remember well.',
    'Describe an older person you admire.',
    'Describe a friend you have known for a long time.',
    'Describe a famous person from your country.',
  ],
  place: [
    'Describe a place you like to visit in your free time.',
    'Describe a city you would like to live in.',
    'Describe a quiet place you enjoy.',
    'Describe a market or shopping place you go to.',
    'Describe a beautiful natural place in your country.',
  ],
  object: [
    'Describe an item of clothing that is important to you.',
    'Describe a piece of technology you use every day.',
    'Describe a gift you received that you really liked.',
    'Describe a book you enjoyed reading.',
    'Describe a meal you enjoyed cooking or eating.',
  ],
  event: [
    'Describe a celebration or festival you attended.',
    'Describe a memorable journey you took.',
    'Describe a time when you helped someone.',
    'Describe a wedding, party or family gathering you remember.',
    'Describe a public event in your country.',
  ],
  experience: [
    'Describe a skill you would like to learn.',
    'Describe a time when you had to make an important decision.',
    'Describe a difficult challenge you overcame.',
    'Describe an interesting conversation you had with someone.',
    'Describe a time you tried something new for the first time.',
  ],
  abstract_future: [
    'Describe a goal you would like to achieve in the next five years.',
    'Describe a change you would like to see in your country.',
    'Describe a job you would like to have one day.',
    'Describe a habit you would like to develop.',
    'Describe an invention you think the world needs.',
  ],
};

export const speakingPart2Data = {
  courseId: 'speaking-part2',
  courseType: 'speaking-course',
  title: 'Part 2: Long Turn',
  icon: FiClock,
  cueCardBank: speakingPart2CueCardBank,
  overview: {
    impact: 'Master the 2-minute monologue across the 6 main cue card families',
    focus: 'Structuring extended responses, transitions, lexical range',
    uses: 'IELTS Speaking Part 2',
    duration: '30-35 minutes',
  },
  sections: [
    {
      title: 'Part 2 Format',
      content: `<p>In Part 2, you receive a cue card and must speak for 1-2 minutes:</p>`,
      points: [
        '1 minute to prepare (use it!)',
        '1-2 minutes to speak',
        'You MUST speak — silence hurts your score',
        'The examiner will not ask questions during your turn',
      ],
      examples: [
        {
          label: 'Sample Cue Card',
          text: 'Describe a book you enjoyed reading.\nYou should say:\n• what the book was\n• when you read it\n• what it was about\n• and explain why you enjoyed it',
        },
      ],
    },
    {
      title: 'Six Cue Card Families',
      content:
        '<p>Most Part 2 cue cards fall into six families. Knowing the family helps you choose vocabulary and structure quickly:</p>',
      table: {
        headers: ['Family', 'Typical Prompt', 'Vocab Anchor'],
        rows: [
          ['Person', 'Describe a person who ...', 'personality adjectives + relationship'],
          ['Place', 'Describe a place you ...', 'location + atmosphere + activities'],
          ['Object', 'Describe an item / gift / book ...', 'sensory details + meaning to you'],
          ['Event', 'Describe a celebration / journey ...', 'time + sequence + feelings'],
          ['Experience', 'Describe a time when you ...', 'narrative tenses + lesson learned'],
          ['Abstract / Future', 'Describe a goal / change / job you would like ...', 'modal verbs + reasons + impact'],
        ],
      },
    },
    {
      title: 'Using Your 1 Minute',
      content: `<p>Make the most of your preparation time:</p>`,
      points: [
        'Read ALL bullet points carefully',
        'Identify the family (person / place / object / event / experience / abstract-future)',
        'Jot down key words (not full sentences)',
        'Plan a structure: intro -> bullet points -> conclusion',
        'Do not try to script — just notes!',
      ],
      examples: [
        {
          label: 'Sample Notes (object family)',
          text: '• "To Kill a Mockingbird"\n• age 15, summer holiday\n• racism, justice, childhood in America\n• powerful message, beautiful writing, memorable characters',
        },
        {
          label: 'Sample Notes (experience family)',
          text: '• decision = move to new city\n• after graduation, age 22\n• scared but excited, family worried\n• freedom, learnt independence, no regrets',
        },
      ],
    },
    {
      title: 'Structuring Your Response',
      content: `<p>Follow this 3-part structure for any Part 2 topic:</p>`,
      table: {
        headers: ['Section', 'Time', 'What to Include'],
        rows: [
          ['Introduction', '15-20 sec', 'State your topic; brief context'],
          ['Main body', '60-90 sec', 'Cover all bullet points with details'],
          ['Conclusion', '15-20 sec', 'Summarise; express feelings or opinions'],
        ],
      },
    },
    {
      title: 'Extending Your Answers',
      content: `<p>Do not just answer — expand each point:</p>`,
      points: [
        'Add reasons: "I enjoyed it because..."',
        'Give examples: "For instance..."',
        'Describe feelings: "It made me feel..."',
        'Add comparisons: "Unlike other books..."',
        'Mention context: "At that time in my life..."',
      ],
      warning:
        'Aim for 2 minutes, but do not panic if you finish at 1:30. Quality matters more than the last 30 seconds.',
    },
    {
      title: 'Family-Specific Phrases',
      content: '<p>One memorable phrase per family lifts Lexical Resource:</p>',
      table: {
        headers: ['Family', 'Useful phrase'],
        rows: [
          ['Person', '"What stands out about her is ..."'],
          ['Place', '"What I love about this place is the ..."'],
          ['Object', '"It holds a special place for me because ..."'],
          ['Event', '"What I remember most clearly is the moment when ..."'],
          ['Experience', '"Looking back, I realise that ..."'],
          ['Abstract / Future', '"Ideally, I would like to ... within the next ... years."'],
        ],
      },
    },
  ],
  exercises: [
    {
      title: 'Identify the Family',
      type: 'select',
      instructions: 'For each cue card, choose the correct family.',
      questions: [
        {
          text: '"Describe a teacher you remember well."',
          options: ['Person', 'Place', 'Experience'],
          answer: 'Person',
          explanation: 'Person family — you describe who they are, what they did, why they matter.',
        },
        {
          text: '"Describe a time when you had to make an important decision."',
          options: ['Object', 'Experience', 'Abstract / Future'],
          answer: 'Experience',
          explanation: 'Experience family — narrative tenses (past simple + past perfect) + lesson learned.',
        },
        {
          text: '"Describe a city you would like to live in."',
          options: ['Place', 'Person', 'Abstract / Future'],
          answer: 'Place',
          explanation:
            'Place family with a future twist — describe attributes of a place + use modal verbs ("I would live there because ...").',
        },
        {
          text: '"Describe a goal you would like to achieve in the next five years."',
          options: ['Experience', 'Abstract / Future', 'Object'],
          answer: 'Abstract / Future',
          explanation:
            'Abstract / Future family — modal verbs ("I would like to ...", "I plan to ...") and reasons.',
        },
      ],
    },
    {
      title: 'Make Quick Notes',
      type: 'writing',
      instructions: 'Create quick notes for this cue card (imagine you have 1 minute).',
      questions: [
        {
          text: 'Describe a place you like to visit in your free time.',
          example:
            '• local park - Central Park\n• weekends, mornings\n• trees, lake, jogging paths\n• peaceful, nature, escape city stress',
          tip: 'Use short phrases, not sentences.',
        },
        {
          text: 'Describe a skill you would like to learn.',
          example:
            '• playing guitar\n• always loved music\n• express emotions, play favourite songs\n• creative outlet, impress friends, relaxation',
          tip: 'Think about what, why, and how.',
        },
        {
          text: 'Describe a celebration or festival you attended.',
          example:
            '• cousin\'s wedding, last summer\n• big garden, lights, music\n• traditional dances, family I had not seen\n• proud, happy, reconnected',
          tip:
            'Include a time anchor (last summer / two years ago) and at least one sensory detail.',
        },
        {
          text: 'Describe a piece of technology you use every day.',
          example:
            '• smartphone\n• every morning, news + maps + messages\n• useful for navigation, music while running\n• can\'t imagine life without it',
          tip: 'Mention at least three concrete uses + how it changed your routine.',
        },
      ],
    },
    {
      title: 'Create Smooth Transitions',
      type: 'select',
      instructions: 'Choose the best phrase to move between bullet points.',
      questions: [
        {
          text: 'Moving from "what it is" to "when you did it"',
          options: [
            '"I first tried this about..."',
            '"Speaking of..."',
            '"In my opinion..."',
          ],
          answer: '"I first tried this about..."',
          explanation: 'This naturally introduces the time element.',
        },
        {
          text: 'Moving from description to explaining why you enjoyed it',
          options: [
            '"The reason I enjoyed this so much is..."',
            '"However..."',
            '"Firstly..."',
          ],
          answer: '"The reason I enjoyed this so much is..."',
          explanation: "This signals you're explaining your feelings.",
        },
        {
          text: 'Moving from a description of a place to your feelings about it',
          options: [
            '"What I love about this place is ..."',
            '"On the other hand, ..."',
            '"To summarise, ..."',
          ],
          answer: '"What I love about this place is ..."',
          explanation:
            '"What I love about ..." is a natural way to shift from facts to feelings without a hard break.',
        },
        {
          text: 'Closing the answer (last 10-15 seconds)',
          options: [
            '"All in all, it is something I will remember for a long time."',
            '"I am not sure what else to say."',
            '"Let me start again from the beginning."',
          ],
          answer: '"All in all, it is something I will remember for a long time."',
          explanation:
            '"All in all, ..." is a clean closing transition that signals the end without abrupt silence.',
        },
      ],
    },
    {
      title: 'Choose the Stronger Sentence',
      type: 'select',
      instructions:
        'Both sentences are correct, but one shows more lexical range. Pick it.',
      questions: [
        {
          text:
            'Topic: a person you admire.\nA. "He is a very nice person."\nB. "He has a remarkable ability to stay calm under pressure."',
          options: ['A', 'B'],
          answer: 'B',
          explanation:
            '"Remarkable ability to stay calm under pressure" shows lexical range (collocation + abstract noun) — more Band 7+.',
        },
        {
          text:
            'Topic: a memorable journey.\nA. "It was a really good trip."\nB. "Looking back, I realise it was one of the most formative experiences I have had."',
          options: ['A', 'B'],
          answer: 'B',
          explanation:
            '"Formative experience" + "looking back, I realise" demonstrates reflection and abstract vocabulary.',
        },
      ],
    },
    {
      title: 'Plan a Full Answer',
      type: 'writing',
      instructions:
        'Write a 6-line plan (intro / bullet 1 / bullet 2 / bullet 3 / bullet 4 / conclusion) for the cue card below.',
      questions: [
        {
          text:
            'Cue card: "Describe a difficult challenge you overcame.\nYou should say:\n• what the challenge was\n• when it happened\n• how you overcame it\n• and explain how you felt afterwards"',
          example:
            'Intro: I want to talk about the time I had to give my first public presentation.\nBullet 1: Big presentation in front of 200 people, last year of university.\nBullet 2: Three months ago, end of my final year.\nBullet 3: Practised every evening, asked a mentor for feedback, used breathing techniques.\nBullet 4: Felt proud, more confident; realised preparation matters more than talent.\nConclusion: Looking back, that experience taught me how to handle stressful situations.',
          tip:
            'Each line should be ONE short sentence — not a full paragraph.',
        },
      ],
    },
  ],
  tips: [
    'Practice speaking for exactly 2 minutes on various topics',
    'Use your notes as prompts, not a script',
    'Identify the cue card family in the first 10 seconds — it controls your vocabulary choices',
    'Include personal examples and feelings',
    'Do not memorise answers — examiners will notice',
    'End with a closing transition ("All in all, ...") rather than silence',
  ],
  dailyPractice: [
    'Practice 3-5 Part 2 topics per day, rotating through the 6 families',
    'Time yourself strictly (1 min prep, 2 min speaking)',
    'Record and review your responses; count filler words ("um", "like")',
    'Practice with Liwaza AI Speaking',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext:
    'Part 2 Long Turn: Structuring 2-minute monologues by identifying the cue card family (person, place, object, event, experience, abstract / future), using preparation time, covering all bullet points, and extending answers with details and reflective lexical range.',
};

// difficulty 1-2: simplified, fewer sections + smaller exercise sets
// difficulty 3: base
// difficulty 4-5: full + advanced tips
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...speakingPart2Data,
      title: speakingPart2Data.title + ' (Foundations)',
      overview: {
        ...speakingPart2Data.overview,
        duration: '20-25 minutes',
      },
      sections: speakingPart2Data.sections.slice(0, 3),
      exercises: [
        {
          ...speakingPart2Data.exercises[0],
          title: 'Identify the Family (Basic)',
          questions: speakingPart2Data.exercises[0].questions.slice(0, 3),
        },
        {
          ...speakingPart2Data.exercises[1],
          title: 'Make Quick Notes (Basic)',
          questions: speakingPart2Data.exercises[1].questions.slice(0, 2),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...speakingPart2Data,
      title: speakingPart2Data.title + ' (Advanced)',
      overview: {
        ...speakingPart2Data.overview,
        duration: '35-40 minutes',
      },
      sections: speakingPart2Data.sections,
      exercises: speakingPart2Data.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(speakingPart2Data.tips || []),
        'For Band 8+: integrate one nuanced expression per bullet (e.g., "what stands out", "looking back I realise", "by no means").',
        'Advanced tip: practise self-correction in flow — at Band 8+, examiners reward fluency even with on-the-fly tweaks.',
      ],
    };
  }
  return speakingPart2Data;
}
